import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CalendarIcon, Wrench } from 'lucide-react';
import { DatePickerField } from './DatePickerField';

const VEHICLE_TYPES = [
  { value: 'bakkie', label: 'Bakkie' }, { value: 'truck', label: 'Truck' },
  { value: 'van', label: 'Van' }, { value: 'car', label: 'Car' },
  { value: 'bus', label: 'Bus' }, { value: 'motorcycle', label: 'Motorcycle' },
  { value: 'trailer', label: 'Trailer' }, { value: 'forklift', label: 'Forklift' },
  { value: 'excavator', label: 'Excavator' }, { value: 'generator', label: 'Generator' },
  { value: 'other', label: 'Other' },
];

const USAGE_UNITS = [
  { value: 'kilometer', label: 'Kilometers (km)' },
  { value: 'hours', label: 'Hours (hrs)' },
  { value: 'date', label: 'Service Dates (timeline)' },
];

const SERVICE_INTERVALS = [
  { value: 5000, label: 'Every 5,000 km' }, { value: 10000, label: 'Every 10,000 km' },
  { value: 15000, label: 'Every 15,000 km' }, { value: 20000, label: 'Every 20,000 km' },
  { value: 30000, label: 'Every 30,000 km' },
];

const SERVICE_INTERVALS_HOURS = [
  ...[150, 200, 250, 300, 350, 400, 450, 500].map((h) => ({ value: h, label: `Every ${h} hrs` })),
  { value: 1000, label: 'Every 1,000 hrs' }, { value: 2000, label: 'Every 2,000 hrs' },
];

const VehicleFormDialog = ({ open, onOpenChange, formData, setFormData, selectedVehicle, saving, onSubmit, drivers = [] }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-heading">{selectedVehicle ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle>
        <DialogDescription>{selectedVehicle ? 'Update vehicle information' : 'Enter details for the new vehicle'}</DialogDescription>
      </DialogHeader>
      <form onSubmit={onSubmit} className="space-y-4 mt-4">
        <div className="space-y-2">
          <Label htmlFor="name">Vehicle Name *</Label>
          <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Delivery Truck 01" data-testid="vehicle-name-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="registration">Registration Number *</Label>
          <Input id="registration" value={formData.registration} onChange={(e) => setFormData({ ...formData, registration: e.target.value })} placeholder="e.g., ABC-1234" className="font-data" data-testid="vehicle-registration-input" />
        </div>
        <div className="space-y-2">
          <Label>Assigned Driver</Label>
          <Select
            value={formData.assigned_driver_id || 'none'}
            onValueChange={(value) => { if (!value) return; setFormData({ ...formData, assigned_driver_id: value === 'none' ? '' : value }); }}
          >
            <SelectTrigger data-testid="vehicle-assigned-driver-select"><SelectValue placeholder="No driver" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No driver</SelectItem>
              {drivers.filter((d) => d.status !== 'inactive' || d.id === formData.assigned_driver_id).map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}{d.assigned_vehicle_name && d.assigned_vehicle_id !== selectedVehicle?.id ? ` (now on ${d.assigned_vehicle_name})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500">A driver can be assigned to one vehicle at a time; picking one here moves them from any other vehicle.</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="type">Vehicle Type *</Label>
          <Select value={formData.vehicle_type} onValueChange={(value) => {
            const hoursBasedTypes = ['forklift', 'excavator', 'generator'];
            const dateBasedTypes = ['trailer'];
            let nextUsage = formData.usage_unit;
            if (dateBasedTypes.includes(value)) nextUsage = 'date';
            else if (hoursBasedTypes.includes(value)) nextUsage = 'hours';
            else if (formData.usage_unit === 'date') nextUsage = 'kilometer';
            setFormData({ ...formData, vehicle_type: value, usage_unit: nextUsage });
          }}>
            <SelectTrigger data-testid="vehicle-type-select"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>{VEHICLE_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="usage_unit">Usage Tracking *</Label>
          <Select value={formData.usage_unit || 'kilometer'} onValueChange={(value) => setFormData({ ...formData, usage_unit: value })}>
            <SelectTrigger data-testid="usage-unit-select"><SelectValue placeholder="Select tracking type" /></SelectTrigger>
            <SelectContent>{USAGE_UNITS.map((u) => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-xs text-slate-500">
            {formData.usage_unit === 'hours' && 'Track usage by hours (for forklifts, generators, etc.)'}
            {formData.usage_unit === 'date' && 'Service by date only — no odometer/hours (ideal for trailers)'}
            {(!formData.usage_unit || formData.usage_unit === 'kilometer') && 'Track usage by kilometers (for road vehicles)'}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="make">Make</Label>
            <Input id="make" value={formData.make} onChange={(e) => setFormData({ ...formData, make: e.target.value })} placeholder="e.g., Toyota" data-testid="vehicle-make-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="model">Model</Label>
            <Input id="model" value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })} placeholder="e.g., Hilux" data-testid="vehicle-model-input" />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="year">Year</Label>
          <Input id="year" type="number" value={formData.year} onChange={(e) => setFormData({ ...formData, year: e.target.value })} placeholder="e.g., 2022" min="1900" max="2100" data-testid="vehicle-year-input" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="vin">VIN Number</Label>
            <Input id="vin" value={formData.vin} onChange={(e) => setFormData({ ...formData, vin: e.target.value.toUpperCase() })} placeholder="e.g., 1HGBH41JXMN109186" className="font-data" data-testid="vehicle-vin-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="engine_number">Engine Number</Label>
            <Input id="engine_number" value={formData.engine_number} onChange={(e) => setFormData({ ...formData, engine_number: e.target.value.toUpperCase() })} placeholder="e.g., 2GR-FE1234567" className="font-data" data-testid="vehicle-engine-input" />
          </div>
        </div>
        {formData.usage_unit !== 'date' && (
          <div className="space-y-2">
            <Label htmlFor="current_reading">{formData.usage_unit === 'hours' ? 'Current Hours' : 'Current Odometer (km)'}</Label>
            {formData.usage_unit === 'hours' ? (
              <Input id="current_hours" type="number" step="0.1" value={formData.current_hours} onChange={(e) => setFormData({ ...formData, current_hours: e.target.value })} placeholder="e.g., 1250.5" className="font-data" data-testid="vehicle-current-hours-input" />
            ) : (
              <Input id="current_odometer" type="number" value={formData.current_odometer} onChange={(e) => setFormData({ ...formData, current_odometer: e.target.value })} placeholder="e.g., 45000" className="font-data" data-testid="vehicle-current-odometer-input" />
            )}
          </div>
        )}
        <div className="border-t pt-4 mt-4">
          <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2"><CalendarIcon className="w-4 h-4" />License & Compliance</h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>License Expiry Date</Label>
              <DatePickerField
                value={(() => {
                  const v = formData.license_expiry;
                  if (!v) return null;
                  if (v instanceof Date) return v.toISOString().split('T')[0];
                  return String(v).slice(0, 10);
                })()}
                onChange={(iso) => setFormData({ ...formData, license_expiry: iso || null })}
                placeholder="Pick expiry date"
                className="w-full"
                triggerTestId="license-expiry-input"
              />
              <p className="text-xs text-slate-500">You&apos;ll receive reminders at 90 / 60 / 30 days and on the expiry day</p>
            </div>
            <div className="space-y-2">
              <Label>Renewal Fee (R)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="e.g., 780"
                value={formData.license_renewal_cost ?? ''}
                onChange={(e) => setFormData({
                  ...formData,
                  license_renewal_cost: e.target.value === '' ? null : parseFloat(e.target.value),
                })}
                className="font-data"
                data-testid="license-renewal-cost-input"
              />
              <p className="text-xs text-slate-500">Used in the digest to sum total cash needed to renew everything in the next 90 days</p>
            </div>
          </div>
        </div>
        <div className="border-t pt-4 mt-4">
          <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2"><Wrench className="w-4 h-4" />Service Tracking</h4>
          {formData.usage_unit === 'date' ? (
            <div className="space-y-4" data-testid="service-timeline-section">
              <div className="bg-blue-50 border-2 border-blue-200 rounded-sm p-3">
                <p className="text-xs font-bold uppercase tracking-wider text-blue-900 mb-1">[ Date-based Service Timeline ]</p>
                <p className="text-sm text-blue-800">For trailers and date-tracked assets — enter the last service date and the next planned service date directly.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Last Service Date</Label>
                  <Input
                    type="date"
                    value={formData.last_service_date || ''}
                    onChange={(e) => setFormData({ ...formData, last_service_date: e.target.value || null })}
                    className="font-data"
                    data-testid="last-service-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Next Service Date *</Label>
                  <Input
                    type="date"
                    value={formData.next_service_expected_date || ''}
                    onChange={(e) => setFormData({ ...formData, next_service_expected_date: e.target.value || null })}
                    min={formData.last_service_date || undefined}
                    className="font-data"
                    data-testid="next-service-date-input"
                  />
                </div>
              </div>
              {formData.last_service_date && formData.next_service_expected_date && (
                <p className="text-xs text-slate-600 font-mono">
                  Service interval: {Math.max(0, Math.round((new Date(formData.next_service_expected_date) - new Date(formData.last_service_date)) / (1000 * 60 * 60 * 24)))} days
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Service Interval</Label>
                {formData.usage_unit === 'hours' ? (
                  <Select value={formData.service_interval_hours ? formData.service_interval_hours.toString() : '500'} onValueChange={(v) => setFormData({ ...formData, service_interval_hours: parseInt(v) })}>
                    <SelectTrigger data-testid="service-interval-select"><SelectValue placeholder="Select interval" /></SelectTrigger>
                    <SelectContent>{SERVICE_INTERVALS_HOURS.map((i) => <SelectItem key={i.value} value={i.value.toString()}>{i.label}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Select value={formData.service_interval_km?.toString()} onValueChange={(v) => setFormData({ ...formData, service_interval_km: parseInt(v) })}>
                    <SelectTrigger data-testid="service-interval-select"><SelectValue placeholder="Select interval" /></SelectTrigger>
                    <SelectContent>{SERVICE_INTERVALS.map((i) => <SelectItem key={i.value} value={i.value.toString()}>{i.label}</SelectItem>)}</SelectContent>
                  </Select>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Last Service Date</Label>
                  <Input type="date" value={formData.last_service_date || ''} onChange={(e) => setFormData({ ...formData, last_service_date: e.target.value || null })} className="font-data" data-testid="last-service-date-input" />
                </div>
                <div className="space-y-2">
                  <Label>{formData.usage_unit === 'hours' ? 'Last Service Hours' : 'Last Service Odometer'}</Label>
                  {formData.usage_unit === 'hours' ? (
                    <Input type="number" step="0.1" value={formData.last_service_hours || ''} onChange={(e) => setFormData({ ...formData, last_service_hours: e.target.value })} placeholder="e.g., 1000" className="font-data" data-testid="last-service-hours-input" />
                  ) : (
                    <Input type="number" value={formData.last_service_odometer || ''} onChange={(e) => setFormData({ ...formData, last_service_odometer: e.target.value })} placeholder="e.g., 50000" className="font-data" data-testid="last-service-odometer-input" />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="border-t pt-4 mt-4">
          <div className="flex items-start gap-3">
            <Checkbox
              id="include_in_fleet_avg"
              checked={formData.include_in_fleet_avg !== false}
              onCheckedChange={(checked) => setFormData({ ...formData, include_in_fleet_avg: checked === true })}
              data-testid="include-in-fleet-avg-checkbox"
              className="mt-1"
            />
            <div className="space-y-1">
              <Label htmlFor="include_in_fleet_avg" className="cursor-pointer">
                Include in fleet Km/L average
              </Label>
              <p className="text-xs text-slate-500">
                Untick for private vehicles so their fuel efficiency doesn&apos;t skew the company average.
              </p>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" className="bg-gray-800 hover:bg-gray-700" disabled={saving} data-testid="save-vehicle-btn">
            {saving ? 'Saving...' : (selectedVehicle ? 'Update' : 'Add Vehicle')}
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

export default VehicleFormDialog;
export { VEHICLE_TYPES, USAGE_UNITS, SERVICE_INTERVALS, SERVICE_INTERVALS_HOURS };
